import { appLogger } from '@/utils/app-logger/appLogger';
import { UNDEFINED } from '@/utils/constants/common';
import { isCmpSdkLoaded } from '@/utils/tracking/isCmpSdkLoaded';
import {
    getCachedCmp,
    initCmpConsentBridge,
    subscribeCmpConsent,
} from '@/utils/vendors/cmpConsentBridge';
import { getCmpConsentBridgeState } from '@/utils/vendors/cmpConsentBridgeState';

const COMPONENT = 'loadTealiumClient';

// Covers optanon's internal 4.5s CMP timeout with slack; matches the 15s CMP registration
// timeout in useConsentTracking.
export const TEALIUM_WATCHDOG_MS = 15_000;

/** Total attempts (initial + retries) before giving up on fetching utag.js. */
export const TEALIUM_MAX_LOAD_ATTEMPTS = 3;

/** First retry delay; doubles per attempt. */
export const TEALIUM_RETRY_BASE_MS = 1_000;

// utag.js creates window.dataHub; retry failed fetches so tracking can recover.
const appendUtagScript = (attempt: number) => {
    const script = document.createElement('script');
    script.src = window.tealiumEnv;
    script.type = 'text/javascript';
    script.async = true;

    script.onload = () => {
        appLogger.info('Tealium utag.js loaded', { component: COMPONENT, attempt });
    };

    script.onerror = () => {
        script.remove();

        if (attempt >= TEALIUM_MAX_LOAD_ATTEMPTS) {
            appLogger.error('Tealium utag.js failed to load; dataHub will never be created', {
                component: COMPONENT,
                attempts: attempt,
                src: window.tealiumEnv,
            });
            return;
        }

        const retryDelayMs = TEALIUM_RETRY_BASE_MS * 2 ** (attempt - 1);
        appLogger.warn('Tealium utag.js failed to load; retrying', {
            component: COMPONENT,
            attempt,
            retryDelayMs,
        });
        window.setTimeout(() => appendUtagScript(attempt + 1), retryDelayMs);
    };

    document.body.appendChild(script);
};

// The `utagSet` latch stays held for the whole retry chain, so the losing loader path cannot
// start a second chain and overshoot TEALIUM_MAX_LOAD_ATTEMPTS.
const injectUtag = () => {
    if (window.utagSet) {
        return;
    }
    // ZONES / SPECULATION RULES: with cross-zone prerendering enabled, this whole module runs
    // inside a hidden renderer for a page the user has not navigated to — and may never
    // navigate to. utag.js auto-fires a page view as soon as it loads, so injecting here would
    // count a view for an unvisited page and stamp it before the navigation. Deferring
    // injection to activation is enough to keep every beacon out of the prerender: nothing in
    // the app reaches Tealium except through `window.readDataLayerPageUpdate` /
    // `readDataLayerEvent`, which utag.js itself defines, and every caller already no-ops when
    // they are absent. adobeDataLayer pushes made while prerendering just accumulate in the
    // ACDL array and are read once the tag arrives.
    if (document.prerendering) {
        document.addEventListener('prerenderingchange', () => injectUtag(), { once: true });
        return;
    }
    if (!window.tealiumEnv) {
        appLogger.warn('Tealium not injected: window.tealiumEnv is missing', {
            component: COMPONENT,
        });
        return;
    }
    window.utagSet = true;

    appendUtagScript(1);
};

/**
 * Loads Tealium (utag.js from `window.tealiumEnv`) without hard-gating it on the CMP stack,
 * so a CMP failure cannot silently kill analytics.
 *
 * Normal path (consent gating preserved): bridge `initialConsent` → inject. Optanon's panic
 * fail-open also resolves through this callback.
 *
 * Watchdog: after {@link TEALIUM_WATCHDOG_MS}, injects utag anyway if optanon never loaded,
 * or it loaded but no CMP SDK (OneTrust/Ketch) ever did. It does not fire while a healthy
 * CMP is awaiting banner interaction.
 *
 * Prerender: while `document.prerendering` is true, both paths defer injection to the
 * `prerenderingchange` event so a speculatively prerendered page fires no analytics until the
 * user actually navigates to it. No-op outside Chromium, where the flag is undefined.
 *
 * Call after `window.tealiumEnv` is set (client init path in `_app`). Injects at most once
 * per page session (`window.utagSet`), whichever path wins; a failed script fetch retries
 * up to {@link TEALIUM_MAX_LOAD_ATTEMPTS} times within that single latched chain.
 */
export const loadTealiumClient = () => {
    if (typeof window === UNDEFINED || window.tealiumLoaderInitialized) {
        return;
    }
    window.tealiumLoaderInitialized = true;
    initCmpConsentBridge();

    // True once optanon has loaded and run the queued callbacks; the bootstrap stub only
    // buffers them.
    let optanonReady = false;

    let unsubscribeScriptsReady: (() => void) | undefined;
    let unsubscribeInitialConsent: (() => void) | undefined;

    const teardownSubscriptions = () => {
        unsubscribeScriptsReady?.();
        unsubscribeScriptsReady = undefined;
        unsubscribeInitialConsent?.();
        unsubscribeInitialConsent = undefined;
    };

    const watchdogId = window.setTimeout(() => {
        if (optanonReady && isCmpSdkLoaded()) {
            return;
        }
        appLogger.warn('CMP unavailable within watchdog window; loading Tealium fail-open', {
            component: COMPONENT,
            optanonReady,
        });
        injectUtag();
        teardownSubscriptions();
    }, TEALIUM_WATCHDOG_MS);

    unsubscribeScriptsReady = subscribeCmpConsent('scriptsReady', () => {
        optanonReady = true;
    });

    unsubscribeInitialConsent = subscribeCmpConsent('initialConsent', () => {
        window.clearTimeout(watchdogId);
        injectUtag();
        teardownSubscriptions();
    });

    // Bridge replay is synchronous — the handler above may run before unsubscribe fns are assigned.
    if (getCmpConsentBridgeState().firedEvents.has('initialConsent') && getCachedCmp()) {
        teardownSubscriptions();
    }
};
